/**
 * CTA tone → solid brand fill.
 *
 * A CTA's optional `tone` (schema: `CTA_TONE_VALUES`) overrides the slot's
 * default treatment with a solid fill in that brand color. Shared by the hero
 * and cta_band renderers so the vocabulary can't drift between them.
 *
 * Tone-fill deliberately WINS over the dark-band CTA inversion
 * (`DARK_BAND_CTA_VARS` in `styleOverrides.ts`). That inversion exists to stop
 * a brand-colored `.cta-primary` from vanishing into a dark band — but an
 * explicit tone is the author naming the exact color they want, so the classes
 * REPLACE `.cta-primary` / `.cta-secondary` rather than layering over them and
 * the `--cta-*` vars never come into play. Same rule the on-dark inversion doc
 * already states: tone-fill buttons are out of its scope.
 */
export type CtaTone = 'accent' | 'primary' | 'secondary'

const TONE_FILL: Record<CtaTone, string> = {
  accent: 'bg-[var(--color-cta)] text-[var(--color-cta-text)]',
  primary: 'bg-[var(--color-primary)] text-[var(--color-primary-text)]',
  secondary: 'bg-[var(--color-secondary)] text-[var(--color-secondary-text)]',
}

/** Fill classes for a set tone, or null to keep the slot's default treatment. */
export const toneFill = (tone?: CtaTone): string | null => (tone ? TONE_FILL[tone] : null)

/**
 * Secondary-CTA treatment over media or a dark surface.
 *
 * The outline is the BRAND ACCENT, not white. White is the safe generic — it
 * reads on any backdrop — but it also renders every tenant's secondary button
 * identically, and next to an accent-filled primary it makes the pair look
 * like it came from two different brands. `--cta-on-dark-accent` (layout.tsx)
 * is the accent pre-floored to 4.5:1 against black, so the brand hue survives
 * without the legibility risk that made white the original default; the `#fff`
 * fallback keeps any context that doesn't set the token on the old behavior.
 *
 * The LABEL stays white. Accent-on-media is a reliable edge but not reliable
 * body text — a mid-tone accent that clears 4.5:1 as a 1px rule can still read
 * poorly as a filled word over moving footage.
 */
export const ON_DARK_OUTLINE =
  'border border-[var(--cta-on-dark-accent,#ffffff)] text-white hover:bg-white/10'
